/**
 * shell/LoginView.tsx —— 登录页（U-L1 按旧树真源重做；装配面承 CONTRACT §6／§2 会话面）。
 *
 * 形态真源＝102 生产树 frontend_src/src/views/LoginView.vue（146 行，逐条落实于 TASK_UL1 §2）。
 * 令牌映射（旧树 ⇒ 本树；**零新令牌**，全部现有名）：
 *  · 卡片圆角 --xg-radius-overlay ⇒ --xg-radius-md；软阴影 --xg-elevation-soft ⇒ --xg-elevation-panel；
 *  · 页底渐变 --xg-surface-base / --xg-blue-100 ⇒ --xg-surface-base / --xg-blue-100；
 *  · 输入圆角 --xg-radius-item ⇒ --xg-radius-sm；聚焦边框 --xg-text-secondary ⇒ --xg-text-secondary（中性深灰，避蓝）；
 *  · 聚焦光晕（旧树 8% 中性黑）⇒ 本树禁字面色（tests/theme.test.ts「禁硬编码色值」扫 src 全量，
 *    任何色值字面量都会破门）⇒ 用现有中性描边令牌 --xg-border-base 作 4px 光晕；
 *  · 错误条 el-alert type=error show-icon ⇒ --xg-state-danger 描边/字色 ＋ 图标（⚠）。
 *
 * 口径（不动）：只提交 `user`／`password`，经 transport/session.login()（唯一出网入口）；
 * 不存 token、不写 localStorage、不自报身份（主体取服务端回执）。
 */
import React, { useState } from "react";
import { login, SessionError } from "../transport/session";
import type { FetchLike } from "../transport/session";
import { tokenVar } from "../theme/tokens";

export interface LoginViewProps {
  /** 登录成功回调（参数＝服务端回执的主体） */
  onLoggedIn: (actor: string) => void;
  fetchFn?: FetchLike;
}

/** 品牌 logo（102 真源件拷贝入 src/assets/，随 K26·D4 改名为 sononux-avatar.svg，禁自造图形） */
const AVATAR_URL = new URL("../assets/sononux-avatar.svg", import.meta.url).href;

type FieldId = "user" | "password";

/** 输入框样式：真 border ＋ 令牌底色 ＋ 聚焦 4px 中性光晕；禁浏览器默认立体边/灰底（appearance:none 覆盖） */
function fieldStyle(focused: boolean, extra: React.CSSProperties = {}): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    minHeight: tokenVar("hit-target"),
    padding: tokenVar("space-xs") + " " + tokenVar("space-sm"),
    fontSize: tokenVar("font-size-content"),
    lineHeight: tokenVar("line-height-content"),
    color: tokenVar("text-primary"),
    background: tokenVar("surface-raised"),
    border: "1px solid " + (focused ? tokenVar("text-secondary") : tokenVar("border-base")),
    borderRadius: tokenVar("radius-sm"),
    appearance: "none",
    boxShadow: focused ? "0 0 0 4px " + tokenVar("border-base") : "none",
    ...extra,
  };
}

/** 主按钮（primary 全宽）；busy ⇒ 降级为禁用态 */
function submitStyle(busy: boolean): React.CSSProperties {
  return {
    width: "100%",
    minHeight: tokenVar("hit-target"),
    padding: "0 " + tokenVar("space-md"),
    border: "1px solid " + (busy ? tokenVar("border-base") : tokenVar("accent")),
    borderRadius: tokenVar("radius-sm"),
    background: busy ? tokenVar("surface-overlay") : tokenVar("accent"),
    color: busy ? tokenVar("text-tertiary") : tokenVar("text-inverse"),
    fontSize: tokenVar("font-size-content"),
    fontWeight: tokenVar("fw-medium"),
    cursor: busy ? "default" : "pointer",
  };
}

/** 页面底：渐变（旧树 135deg 双色）＋ flex 居中（水平居中、垂直略偏上＝底部留白） */
const PAGE_STYLE: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: tokenVar("space-md"),
  paddingBottom: tokenVar("space-8"),
  background: "linear-gradient(135deg, " + tokenVar("surface-base") + " 0%, " + tokenVar("blue-100") + " 100%)",
};

/** 卡片：宽 400px、细边 0.5px 令牌色、软阴影、令牌圆角（映射见文件头） */
const CARD_STYLE: React.CSSProperties = {
  width: 400,
  maxWidth: "100%",
  boxSizing: "border-box",
  padding: tokenVar("space-6"),
  background: tokenVar("surface-raised"),
  border: "0.5px solid " + tokenVar("border-base"),
  borderRadius: tokenVar("radius-md"),
  boxShadow: tokenVar("elevation-panel"),
};

/** 品牌行：logo 40×40 ＋ 标题 Sononux；副标题「多模型工程助手」 */
function BrandRow(): React.ReactElement {
  return (
    <header>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: tokenVar("space-xs") }}>
        <img src={AVATAR_URL} alt="Sononux" width={40} height={40} />
        <h1
          data-testid="login-title"
          style={{
            margin: 0,
            fontSize: tokenVar("font-l"),
            lineHeight: tokenVar("lh-l"),
            fontWeight: tokenVar("fw-semibold"),
            color: tokenVar("text-primary"),
          }}
        >
          Sononux
        </h1>
      </div>
      <p
        style={{
          margin: tokenVar("space-2xs") + " 0 " + tokenVar("space-6"),
          textAlign: "center",
          color: tokenVar("text-tertiary"),
          fontSize: tokenVar("font-size-content"),
        }}
      >
        多模型工程助手
      </p>
    </header>
  );
}

/** 错误态：el-alert type=error show-icon 形态（role=alert ＋ 图标 ＋ 醒目描边） */
function ErrorAlert({ message }: { message: string }): React.ReactElement {
  return (
    <div
      data-testid="login-error"
      role="alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: tokenVar("space-xs"),
        marginTop: tokenVar("space-sm"),
        padding: tokenVar("space-xs") + " " + tokenVar("space-sm"),
        border: "1px solid " + tokenVar("state-danger"),
        borderRadius: tokenVar("radius-sm"),
        background: tokenVar("surface-overlay"),
        color: tokenVar("state-danger"),
        fontSize: tokenVar("font-size-content"),
        lineHeight: tokenVar("line-height-content"),
      }}
    >
      <span aria-hidden="true">⚠</span>
      <span>{message}</span>
    </div>
  );
}

/** 密码可视切换（旧树 show-password；可选项，type=button 防误提交） */
function RevealToggle({ reveal, onToggle }: { reveal: boolean; onToggle: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      data-testid="login-pw-toggle"
      aria-label={reveal ? "隐藏密码" : "显示密码"}
      aria-pressed={reveal}
      onClick={onToggle}
      style={{
        position: "absolute",
        right: tokenVar("space-xs"),
        top: "50%",
        transform: "translateY(-50%)",
        padding: tokenVar("space-2xs"),
        // ★F-09（20261019 真机量测）：命中区 ≥44×44（原 34×27 不达标）
        minWidth: tokenVar("hit-target"),
        minHeight: tokenVar("hit-target"),
        border: "none",
        background: "transparent",
        color: tokenVar("text-tertiary"),
        fontSize: tokenVar("font-size-secondary"),
        cursor: "pointer",
      }}
    >
      {reveal ? "隐藏" : "显示"}
    </button>
  );
}

export default function LoginView({ onLoggedIn, fetchFn }: LoginViewProps): React.ReactElement {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [focus, setFocus] = useState<FieldId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!user || !password) {
      setError("请输入用户名和密码");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const actor = await login(user, password, fetchFn);
      onLoggedIn(actor.actor);
    } catch (err) {
      const msg = err instanceof SessionError
        ? (err.status === 401 ? "用户名或密码不正确" : "登录服务不可用（" + err.status + "）")
        : String(err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-view" style={PAGE_STYLE}>
      <div data-testid="login-card" style={CARD_STYLE}>
        <BrandRow />
        <form onSubmit={submit}>
          <label style={{ display: "block", marginBottom: tokenVar("space-sm") }}>
            <input
              data-testid="login-user"
              aria-label="用户名"
              placeholder="用户名"
              value={user}
              autoComplete="username"
              onChange={(e) => setUser(e.target.value)}
              onFocus={() => setFocus("user")}
              onBlur={() => setFocus((f) => (f === "user" ? null : f))}
              style={fieldStyle(focus === "user")}
            />
          </label>
          <label style={{ display: "block", position: "relative", marginBottom: tokenVar("space-md") }}>
            <input
              data-testid="login-password"
              aria-label="密码"
              placeholder="密码"
              type={reveal ? "text" : "password"}
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocus("password")}
              onBlur={() => setFocus((f) => (f === "password" ? null : f))}
              style={fieldStyle(focus === "password", { paddingRight: tokenVar("space-7") })}
            />
            <RevealToggle reveal={reveal} onToggle={() => setReveal((v) => !v)} />
          </label>
          <button data-testid="login-submit" type="submit" disabled={busy} style={submitStyle(busy)}>
            {busy ? "登录中…" : "登录"}
          </button>
        </form>
        {error ? <ErrorAlert message={error} /> : null}
      </div>
    </div>
  );
}
